import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/client';
import { useApi } from '@/hooks/useApi';
import type {
  InviteChannel,
  OutreachAccount,
  OutreachAccountAuditEvent,
  OutreachAccountBulkResponse,
  OutreachAccountInput,
  OutreachAccountStatus,
  OutreachAccountsList,
} from '@/api/types';

/**
 * Admin-only outreach accounts vault (/admin/outreach-accounts). Nothing on
 * the outreacher surface may import this file.
 *
 * Passwords never go through React Query: list rows only say `hasPassword`,
 * and {@link useRevealAccountPassword} is a plain call whose result lives in
 * the caller's component state for a few seconds, so it never sits in the
 * query cache (or the devtools).
 */

const KEY = ['admin', 'outreach-accounts'] as const;

export interface OutreachAccountFilters {
  /** Outreacher public id (or numeric id). */
  inviterId?: string;
  platform?: InviteChannel | '';
  status?: OutreachAccountStatus | '';
  search?: string;
}

/** The backend answers 503 VAULT_NOT_CONFIGURED until OUTREACH_ACCOUNTS_KEY is set. */
export function isVaultNotConfigured(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'VAULT_NOT_CONFIGURED';
}

export function useOutreachAccounts(filters: OutreachAccountFilters = {}) {
  const api = useApi();
  const query: Record<string, string> = {};
  if (filters.inviterId) query.inviterId = filters.inviterId;
  if (filters.platform) query.platform = filters.platform;
  if (filters.status) query.status = filters.status;
  if (filters.search?.trim()) query.search = filters.search.trim();
  return useQuery({
    queryKey: [...KEY, 'list', query],
    queryFn: () => api.get<OutreachAccountsList>('/admin/outreach-accounts', query),
    placeholderData: (prev) => prev,
  });
}

export function useCreateOutreachAccount() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OutreachAccountInput) => api.post<OutreachAccount>('/admin/outreach-accounts', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateOutreachAccount() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, body }: { publicId: string; body: OutreachAccountInput }) =>
      api.patch<OutreachAccount>(`/admin/outreach-accounts/${publicId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteOutreachAccount() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.del<{ deleted: true }>(`/admin/outreach-accounts/${publicId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useBulkAddOutreachAccounts() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { inviterId: string; rows: OutreachAccountInput[] }) =>
      api.post<OutreachAccountBulkResponse>('/admin/outreach-accounts/bulk', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useOutreachAccountAudit(publicId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: [...KEY, 'audit', publicId],
    enabled: !!publicId,
    queryFn: () =>
      api.get<{ events: OutreachAccountAuditEvent[] }>(`/admin/outreach-accounts/${publicId}/reveals`),
  });
}

/**
 * Fetch one decrypted password. Every call writes an audit row server-side
 * (`purpose` says whether it was shown or copied). Refreshes "last revealed"
 * and the audit history afterwards; the password itself is only returned.
 */
export function useRevealAccountPassword(): (publicId: string, purpose: 'REVEAL' | 'COPY') => Promise<string> {
  const api = useApi();
  const qc = useQueryClient();
  return useCallback(
    async (publicId, purpose) => {
      const res = await api.post<{ password: string }>(`/admin/outreach-accounts/${publicId}/reveal`, { purpose });
      void qc.invalidateQueries({ queryKey: KEY });
      return res.password;
    },
    [api, qc],
  );
}
